import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullModal from '../components/FullModal';
import PageTitle from '../components/PageTitle';
import Loader from '../components/Loader';

const DeleteAccountRequest = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [error, setError] = useState(null);

    const handleEmailChange = (event) => setEmail(event.target.value);

    const sendRequest = async (e) => {
        e?.preventDefault?.();
        if (!email.trim()) {
            setError(t('AccountDeletion.publicRequest.missingEmail'));
            return;
        }
        setFetchStatus('loading');
        setError(null);
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/public-request-deletion`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() })
            });
            const result = await response.json();
            if (response.ok) {
                setFetchStatus('succeeded');
            } else {
                setError(result.message || t('AccountDeletion.publicRequest.genericError'));
                setFetchStatus('failed');
            }
        } catch (err) {
            setError(err.message || t('AccountDeletion.publicRequest.connectionError'));
            setFetchStatus('failed');
        }
    };

    return (
        <FullModal px="4" items="center">
            <PageTitle backLink="/" title={t('AccountDeletion.publicRequest.title')} />

            <div className="flex flex-col items-center gap-4 max-w-xl w-full my-4">
                <p className="text-sm text-neutral-700 text-center">
                    {t('AccountDeletion.publicRequest.intro')}
                </p>
                <p className="text-xs text-neutral-600 text-center">
                    {t('AccountDeletion.publicRequest.graceInfo')}
                </p>

                {fetchStatus !== 'succeeded' && (
                    <form onSubmit={sendRequest} className="flex flex-col items-center gap-2 w-full">
                        <div className="w-full md:w-96 flex flex-col gap-1">
                            <label htmlFor="email" className="text-sm">{t('AccountDeletion.publicRequest.emailLabel')}</label>
                            <input
                                type="email"
                                id="email"
                                className="w-full"
                                value={email}
                                onChange={handleEmailChange}
                                required
                                autoComplete="email"
                            />
                        </div>
                        <div className="my-4">
                            {fetchStatus === 'loading' ? (
                                <Loader />
                            ) : (
                                <button
                                    type="submit"
                                    className="bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-xl"
                                >
                                    {t('AccountDeletion.publicRequest.submit')}
                                </button>
                            )}
                        </div>
                        {fetchStatus === 'failed' && error && (
                            <p className="text-sm text-red-700 text-center">{error}</p>
                        )}
                    </form>
                )}

                {fetchStatus === 'succeeded' && (
                    <div className="flex flex-col items-center gap-4 mt-4">
                        <h3 className="text-center">
                            {t('AccountDeletion.publicRequest.successTitle')}
                        </h3>
                        <p className="text-xs text-neutral-600 text-center">
                            {t('AccountDeletion.publicRequest.successHint')}
                        </p>
                        <Link to="/" className="primary mt-4">{t('AccountDeletion.publicRequest.backHome')}</Link>
                    </div>
                )}

                <div className="mt-8 border-t pt-6 w-full">
                    <h4 className="text-sm font-semibold mb-2">{t('AccountDeletion.publicRequest.deletedSection')}</h4>
                    <ul className="text-xs text-neutral-700 list-disc pl-5 flex flex-col gap-1">
                        <li>{t('AccountDeletion.publicRequest.deletedItem1')}</li>
                        <li>{t('AccountDeletion.publicRequest.deletedItem2')}</li>
                    </ul>
                    <h4 className="text-sm font-semibold mt-4 mb-2">{t('AccountDeletion.publicRequest.anonymizedSection')}</h4>
                    <ul className="text-xs text-neutral-700 list-disc pl-5 flex flex-col gap-1">
                        <li>{t('AccountDeletion.publicRequest.anonymizedItem1')}</li>
                    </ul>
                </div>
            </div>
        </FullModal>
    );
};

export default DeleteAccountRequest;
